import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatINR } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Star, Truck, ShieldCheck, RotateCcw, MapPin } from "lucide-react";

export default function ProductDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState({ reviews: [], average: 0, count: 0 });
  const [size, setSize] = useState(null);
  const [color, setColor] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [qty, setQty] = useState(1);
  const { addItem } = useCart();
  const navigate = useNavigate();

  const [rating, setRating] = useState(5);
  const [reviewTitle, setReviewTitle] = useState("");
  const [reviewBody, setReviewBody] = useState("");
  const [posting, setPosting] = useState(false);

  const loadReviews = () => api.get(`/reviews/${id}`).then((r) => setReviews(r.data));

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => {
      setProduct(r.data);
      setSize(r.data.sizes?.[0] || null);
      setColor(r.data.colors?.[0] || null);
      setActiveImg(0);
    });
    loadReviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!product) return <div className="p-10 text-sm text-[#6b6b6b]">Loading…</div>;

  const finalPrice = Number(product.price) * (1 - Number(product.discount_percent || 0) / 100);
  const hasDiscount = Number(product.discount_percent || 0) > 0;
  const images = product.images?.length ? product.images : [""];

  const validate = () => {
    if (product.sizes?.length && !size) { toast.error("Please select a size"); return false; }
    if (product.colors?.length && !color) { toast.error("Please select a color"); return false; }
    return true;
  };

  const onAdd = () => {
    if (!validate()) return;
    addItem(product, { size, color, quantity: qty });
    toast.success("Added to bag");
  };

  const onBuy = () => {
    if (!validate()) return;
    addItem(product, { size, color, quantity: qty });
    navigate("/checkout");
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.info("Please sign in to write a review");
      navigate("/login", { state: { from: `/product/${id}` } });
      return;
    }
    setPosting(true);
    try {
      await api.post("/reviews", {
        product_id: id,
        rating,
        title: reviewTitle,
        body: reviewBody,
      });
      toast.success("Review submitted");
      setReviewTitle("");
      setReviewBody("");
      setRating(5);
      loadReviews();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit review");
    } finally {
      setPosting(false);
    }
  };

  const avg = reviews.average || 0;
  const full = Math.floor(avg);
  const half = avg - full >= 0.5;

  return (
    <div className="min-h-screen" data-testid="product-detail-page">
      <div className="max-w-[1500px] mx-auto px-3 md:px-6 py-6">
        <div className="card p-4 md:p-6">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {/* Gallery */}
            <div className="md:col-span-5">
              <div className="flex gap-3">
                <div className="hidden md:flex flex-col gap-2 w-16 shrink-0">
                  {images.map((im, i) => (
                    <button
                      key={i}
                      onMouseEnter={() => setActiveImg(i)}
                      onClick={() => setActiveImg(i)}
                      className={`w-16 h-16 border-2 rounded-md overflow-hidden bg-[#faf7f2] ${
                        i === activeImg ? "border-[#c8532e]" : "border-[#e0d8c8]"
                      }`}
                      data-testid={`product-thumb-${i}`}
                    >
                      {im && <img src={im} className="w-full h-full object-cover" alt="" />}
                    </button>
                  ))}
                </div>
                <div className="flex-1 bg-[#faf7f2] border border-[#e0d8c8] rounded-md overflow-hidden">
                  <div className="aspect-[4/5]">
                    {images[activeImg] && (
                      <img src={images[activeImg]} className="w-full h-full object-cover" alt={product.name} data-testid="product-main-image" />
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Info */}
            <div className="md:col-span-4">
              <div className="text-xs text-[#6b6b6b] uppercase tracking-[0.2em]">{product.brand || "shopnest"}</div>
              <h1 className="font-display text-3xl md:text-4xl font-semibold text-[#1a1d29] mt-1 leading-tight" data-testid="product-name">
                {product.name}
              </h1>

              <div className="mt-3 flex items-center gap-2 text-sm">
                <span className="text-[#1a1d29] font-semibold">{avg.toFixed(1)}</span>
                <div className="flex text-[#d4a017]">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <Star
                      key={i}
                      className="w-4 h-4"
                      fill={i < full || (i === full && half) ? "#d4a017" : "transparent"}
                      strokeWidth={1.5}
                    />
                  ))}
                </div>
                <a href="#reviews" className="ec-link">
                  {reviews.count > 0 ? `${reviews.count} rating${reviews.count > 1 ? "s" : ""}` : "Be the first to review"}
                </a>
              </div>

              <div className="border-t border-[#e0d8c8] mt-5 pt-5">
                {hasDiscount && (
                  <div className="inline-block deal-tag mb-2">-{Math.round(product.discount_percent)}%</div>
                )}
                <div className="flex items-baseline gap-3">
                  <span className="font-display text-3xl md:text-4xl font-semibold text-[#1a1d29]" data-testid="product-price">
                    {formatINR(finalPrice)}
                  </span>
                  {hasDiscount && (
                    <span className="text-sm text-[#6b6b6b]">
                      M.R.P. <span className="line-through">{formatINR(product.price)}</span>
                    </span>
                  )}
                </div>
                <div className="text-xs text-[#6b6b6b] mt-1">Inclusive of all taxes</div>
              </div>

              {product.colors?.length > 0 && (
                <div className="mt-6">
                  <div className="text-sm"><span className="font-semibold">Colour:</span> <span>{color}</span></div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {product.colors.map((c) => (
                      <button
                        key={c}
                        onClick={() => setColor(c)}
                        className={`h-10 px-4 border rounded-full text-sm transition-colors ${
                          color === c ? "border-[#c8532e] ring-2 ring-[#c8532e]/40 bg-white" : "border-[#e0d8c8] bg-white hover:border-[#1a1d29]"
                        }`}
                        data-testid={`color-option-${c}`}
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {product.sizes?.length > 0 && (
                <div className="mt-5">
                  <div className="text-sm"><span className="font-semibold">Size:</span> <span>{size}</span></div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {product.sizes.map((s) => (
                      <button
                        key={s}
                        onClick={() => setSize(s)}
                        className={`min-w-[52px] h-10 px-3 border rounded-full text-sm transition-colors ${
                          size === s ? "border-[#c8532e] ring-2 ring-[#c8532e]/40 bg-white" : "border-[#e0d8c8] bg-white hover:border-[#1a1d29]"
                        }`}
                        data-testid={`size-option-${s}`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-6 border-t border-[#e0d8c8] pt-5">
                <div className="text-sm font-semibold text-[#1a1d29] mb-2">About this piece</div>
                <p className="text-sm text-[#1a1d29]" data-testid="product-description">{product.description}</p>
                <ul className="mt-3 text-sm space-y-1 list-disc pl-5">
                  {product.material && <li><span className="font-semibold">Material:</span> {product.material}</li>}
                  {product.brand && <li><span className="font-semibold">Brand:</span> {product.brand}</li>}
                  <li><span className="font-semibold">Category:</span> <span className="capitalize">{product.category}</span></li>
                </ul>
              </div>
            </div>

            {/* Buy box */}
            <div className="md:col-span-3">
              <div className="border border-[#e0d8c8] rounded-md p-4 bg-[#faf7f2]">
                <div className="font-display text-2xl font-semibold text-[#1a1d29]">
                  {formatINR(finalPrice)}
                </div>
                <div className="mt-2 text-sm">
                  FREE delivery <span className="font-semibold">Tomorrow</span>
                </div>
                <div className="mt-1 flex items-center gap-1 text-xs text-[#6b6b6b]">
                  <MapPin className="w-3.5 h-3.5" /> Deliver across India
                </div>

                <div className={`mt-4 text-base font-semibold ${product.stock > 0 ? "text-[#3a6a3a]" : "text-[#b52929]"}`}>
                  {product.stock > 0 ? "In stock" : "Out of stock"}
                </div>

                {product.stock > 0 && (
                  <div className="mt-3">
                    <label className="text-sm">Quantity:</label>
                    <select
                      value={qty}
                      onChange={(e) => setQty(Number(e.target.value))}
                      className="ml-2 border border-[#e0d8c8] rounded-md h-9 px-2 text-sm bg-white"
                      data-testid="product-qty-select"
                    >
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                        <option key={n} value={n}>{n}</option>
                      ))}
                    </select>
                  </div>
                )}

                <button
                  onClick={onAdd}
                  disabled={product.stock <= 0}
                  className="btn-primary mt-4 w-full text-sm"
                  data-testid="add-to-cart-button"
                >
                  Add to Bag
                </button>
                <button
                  onClick={onBuy}
                  disabled={product.stock <= 0}
                  className="btn-ink mt-2 w-full text-sm"
                  data-testid="buy-now-button"
                >
                  Buy Now
                </button>

                <div className="mt-5 pt-4 border-t border-[#e0d8c8] space-y-3 text-xs">
                  <div className="flex items-center gap-2"><Truck className="w-4 h-4 text-[#6b6b6b]" /> Fast, free shipping over ₹5,000</div>
                  <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#6b6b6b]" /> Secure payment via Razorpay</div>
                  <div className="flex items-center gap-2"><RotateCcw className="w-4 h-4 text-[#6b6b6b]" /> 14-day easy returns</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Reviews */}
        <div id="reviews" className="card p-4 md:p-6 mt-6" data-testid="reviews-section">
          <h2 className="font-display text-2xl md:text-3xl font-semibold text-[#1a1d29] mb-4">Reviews & Ratings</h2>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="md:col-span-1">
              <div className="flex items-baseline gap-3">
                <span className="font-display text-5xl font-semibold">{avg.toFixed(1)}</span>
                <span className="text-sm text-[#6b6b6b]">/ 5</span>
              </div>
              <div className="mt-1 flex text-[#d4a017]">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Star
                    key={i}
                    className="w-5 h-5"
                    fill={i < full || (i === full && half) ? "#d4a017" : "transparent"}
                    strokeWidth={1.5}
                  />
                ))}
              </div>
              <div className="text-sm text-[#6b6b6b] mt-1">
                {reviews.count} review{reviews.count !== 1 ? "s" : ""}
              </div>

              <form onSubmit={submitReview} className="mt-6" data-testid="review-form">
                <div className="text-sm font-semibold text-[#1a1d29] mb-2">Write a review</div>
                <div className="flex gap-1 mb-3">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setRating(n)}
                      className="p-0.5"
                      data-testid={`review-star-${n}`}
                      aria-label={`${n} stars`}
                    >
                      <Star
                        className="w-6 h-6 text-[#d4a017]"
                        fill={n <= rating ? "#d4a017" : "transparent"}
                        strokeWidth={1.5}
                      />
                    </button>
                  ))}
                </div>
                <input
                  type="text"
                  placeholder="Review title"
                  value={reviewTitle}
                  onChange={(e) => setReviewTitle(e.target.value)}
                  className="w-full h-10 bg-white border border-[#e0d8c8] rounded-md outline-none px-3 text-sm mb-2"
                  data-testid="review-title-input"
                />
                <textarea
                  placeholder="Share your thoughts"
                  value={reviewBody}
                  onChange={(e) => setReviewBody(e.target.value)}
                  rows={4}
                  className="w-full bg-white border border-[#e0d8c8] rounded-md outline-none px-3 py-2 text-sm mb-3 resize-y"
                  data-testid="review-body-input"
                />
                <button
                  type="submit"
                  disabled={posting}
                  className="btn-primary text-sm w-full"
                  data-testid="review-submit"
                >
                  {posting ? "Submitting…" : "Submit review"}
                </button>
              </form>
            </div>

            <div className="md:col-span-2">
              {reviews.reviews.length === 0 ? (
                <div className="text-sm text-[#6b6b6b] py-6" data-testid="reviews-empty">
                  No reviews yet — be the first to review this piece.
                </div>
              ) : (
                <ul className="divide-y divide-[#e0d8c8]">
                  {reviews.reviews.map((r) => {
                    const rf = Math.floor(r.rating);
                    return (
                      <li key={r.id} className="py-4" data-testid={`review-item-${r.id}`}>
                        <div className="flex items-center gap-2">
                          <div className="flex text-[#d4a017]">
                            {[0, 1, 2, 3, 4].map((i) => (
                              <Star key={i} className="w-4 h-4" fill={i < rf ? "#d4a017" : "transparent"} strokeWidth={1.5} />
                            ))}
                          </div>
                          <span className="font-semibold text-sm">{r.title || "Review"}</span>
                        </div>
                        <div className="text-xs text-[#6b6b6b] mt-1">
                          by {r.user_name} · {new Date(r.created_at).toLocaleDateString()}
                        </div>
                        {r.body && <p className="mt-2 text-sm">{r.body}</p>}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
